import type { ReactNode } from "react";
import { Button } from "../components/Button";
import { Alert, Card, EmptyState, Loading } from "../components/Card";
import { Field, Input, Select } from "../components/Field";
import { Icon } from "../components/Icon";
import { Chip, StatusPill } from "../components/Status";
import { SessionTabs } from "../components/SessionTabs";
import { BellButton } from "../components/Alarm";
import { ThemeToggle } from "../app/AppShell";
import { MyTurnPanel } from "../components/MyTurn";
import type { Issue } from "../lib/tasks";
import "../chat/ChatDrawer.css";
import { tx } from "../i18n";

/* ══ 상태 7종 — 공통 부품 여덟을 한 판에 ══
   기본 · hover · 눌림 · 비활성 · 로딩 · 빈 상태 · 오류(t-in-ds-patterns 수용 기준). 부품의 모양은 공통 v3
   (.b-*)다 — hover · 눌림도 공통 것이라 **정지 화면으로 따라 그리지 않고** 그 칸에 진짜 부품을 세워 손으로
   올리고 눌러 보게 한다(따라 그리면 공통 값을 여기 베끼게 되고, 공통이 바뀌면 이 페이지만 거짓말을 한다).

   **그 부품에 없는 상태는 지어내지 않는다.** 「해당 없음」 칸에 왜 없는지와 그 자리를 무엇이
   대신하는지를 적는다 — 빈 칸으로 두면 빠뜨린 것과 구분이 안 된다. */

const STATES = [
  tx("pat.states.base"),
  "hover",
  tx("pat.states.pressed"),
  tx("pat.states.disabled"),
  tx("pat.states.loading"),
  tx("pat.states.empty"),
  tx("ui.alertHead.error"),
] as const;

function NA({ why }: { why: string }) {
  return (
    <p className="pat__na">
      <b>{tx("pat.states.na")}</b> · {why}
    </p>
  );
}

/** hover · 눌림 칸 — 진짜 부품 + 「올려 주세요」 한 줄. */
function Live({ children, act = "hover" }: { children: ReactNode; act?: "hover" | "press" }) {
  return (
    <>
      {children}
      <span className="caption">{tx(act === "press" ? "pat.states.livePress" : "pat.states.liveHover")}</span>
    </>
  );
}

function Row({ name, cells }: { name: string; cells: ReactNode[] }) {
  return (
    <>
      <h3>{name}</h3>
      <div className="pat__states">
        {STATES.map((s, i) => (
          <div className="pat__state" key={s}>
            <span className="b-eyebrow">{s}</span>
            <div className="pat__statebody">{cells[i]}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function MiniTable({ row, body }: { row?: "selected" | "disabled"; body?: ReactNode }) {
  return (
    <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th>{tx("chat.issue")}</th>
            <th className="is-end">{tx("pat.states.times")}</th>
          </tr>
        </thead>
        <tbody>
          {body ?? (
            <>
              <tr className={row === "selected" ? "is-selected" : row === "disabled" ? "pat__rowoff" : undefined} aria-selected={row === "selected" || undefined} aria-disabled={row === "disabled" || undefined}>
                <td className="is-primary">{tx("pat.sample.fontLoad")}</td>
                <td className="is-end">2</td>
              </tr>
              <tr>
                <td className="is-primary">{tx("pat.sample.navOverflow")}</td>
                <td className="is-end">10</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function StateRow({ children }: { children: ReactNode }) {
  return (
    <tr className="pat__staterow">
      <td colSpan={2}>{children}</td>
    </tr>
  );
}

function Tab({ label, selected, disabled }: { label: string; selected?: boolean; disabled?: boolean }) {
  return (
    <button type="button" role="tab" className={`b-tab${selected ? " is-on" : ""}`} aria-selected={selected ?? false} disabled={disabled}>
      {label}
    </button>
  );
}

function Tabs({ children }: { children: ReactNode }) {
  return (
    <div className="b-tabs" role="tablist" aria-label={tx("pat.sample.label")}>
      {children}
    </div>
  );
}

/** 내 차례 표본 — 화면에 그릴 칸만 채운다(목록 응답의 나머지 칸은 이 부품이 안 읽는다). */
const SAMPLE_BASE = {
  type: "feature", priority: "normal", complexity: "normal", milestoneId: null, parentId: null, childCount: 0,
  doneChildCount: 0, prLink: null, queueRank: null, updatedAt: "2026-10-31T00:00:00Z", openSession: null,
  agentRequestedAt: null, intent: "", acceptance: [], dependsOn: [],
} as const;
const SAMPLE_Q = {
  ...SAMPLE_BASE, id: "t-sample-q", title: tx("pat.states.myTurnQTitle"), status: "blocked", assignee: null, assigneeKind: null,
  openQuestions: [{ id: "q1", prompt: tx("pat.states.myTurnQPrompt"), options: null, askedStatus: "in-progress", createdAt: "2026-10-31T00:00:00Z" }],
} as unknown as Issue;
const SAMPLE_MINE = {
  ...SAMPLE_BASE, id: "t-sample-mine", title: tx("pat.states.myTurnMineTitle"), status: "ready", assignee: "o2hp", assigneeKind: "ticket",
} as unknown as Issue;

function NavBar({ children }: { children?: ReactNode }) {
  return (
    <div className="pat__navbar">
      <a className="b-navlink is-on" href="#/patterns" aria-current="page" onClick={(e) => e.preventDefault()}>
        {tx("pat.sample.patterns")}
      </a>
      <a className="b-navlink" href="#/board" onClick={(e) => e.preventDefault()}>
        {tx("nav.board")}
      </a>
      {children}
    </div>
  );
}

/** 겉창 틀 — 실제 겉창(chat/ChatDrawer.css)의 머리 · 몸 · 발 클래스를 그대로 쓰고 자리만 고정을 푼다. */
function Dock({ children, foot }: { children: ReactNode; foot?: ReactNode }) {
  return (
    <div className="pat__dock">
      <aside className="cdock" aria-label={tx("pat.states.dockLabel")}>
        <header className="cdock__head">
          <span className="cdock__picktext">{tx("chat.issue")}</span>
          <span className="b-badge">t-in-ds-fonts</span>
          <Button variant="ghost" icon aria-label={tx("common.action.close")}>
            <Icon name="x" />
          </Button>
        </header>
        <div className="cdock__scroll">{children}</div>
        <footer className="cdock__foot">{foot ?? <p className="caption">{tx("pat.states.dockFoot")}</p>}</footer>
      </aside>
    </div>
  );
}

const LINE = <p className="muted">● 나루 · claude — 글꼴 서브셋을 만들었다.</p>; // copy-ignore: 견본 자료(나루의 말)

export function PatternStates() {
  return (
    <>
      <Row
        name={tx("pat.states.button")}
        cells={[
          <Button variant="primary">{tx("common.action.save")}</Button>,
          <Live>
            <Button variant="primary">{tx("common.action.save")}</Button>
          </Live>,
          <Live act="press">
            <Button variant="primary">{tx("common.action.save")}</Button>
          </Live>,
          <Button variant="primary" disabled>
            {tx("common.action.save")}
          </Button>,
          <Button variant="primary" loading>
            {tx("common.state.saving")}
          </Button>,
          <NA why={tx("pat.states.na_.btnEmpty")} />,
          <NA why={tx("pat.states.na_.btnError")} />,
        ]}
      />

      <Row
        name={tx("pat.states.input")}
        cells={[
          <Input size="md" defaultValue="https://mops.internal" aria-label={tx("pat.sample.label")} />,
          <Live>
            <Input size="md" defaultValue="https://mops.internal" aria-label={tx("pat.sample.label")} />
          </Live>,
          <>
            <Input size="md" defaultValue="https://mops.internal" aria-label={tx("pat.sample.label")} />
            <span className="caption">{tx("pat.states.focusInstead")}</span>
          </>,
          <Input size="md" defaultValue={tx("pat.sample.locked")} disabled aria-label={tx("pat.sample.label")} />,
          <Select size="md" disabled aria-busy="true" aria-label={tx("pat.sample.label")}>
            <option>{tx("pat.states.listLoading")}</option>
          </Select>,
          <Input size="md" placeholder="https://" aria-label={tx("pat.sample.label")} />,
          <Field label={tx("conn.token")} error={tx("pat.sample.tokenEmpty")}>
            {(id, invalid) => <Input id={id} size="md" invalid={invalid} defaultValue="" />}
          </Field>,
        ]}
      />

      <Row
        name={tx("pat.states.card")}
        cells={[
          <Card title={tx("pat.states.cardTitle")}>
            <p className="muted">ola-mops · {tx("common.status.normal")}</p>
          </Card>,
          <NA why={tx("pat.states.na_.cardHover")} />,
          <NA why={tx("pat.states.na_.cardPress")} />,
          <NA why={tx("pat.states.na_.cardDisabled")} />,
          <Card title={tx("pat.states.cardTitle")}>
            <Loading />
          </Card>,
          <Card title={tx("pat.states.cardTitle")}>
            <EmptyState title={tx("pat.states.cardEmpty")} desc={tx("pat.states.cardEmptyDesc")} />
          </Card>,
          <Card title={tx("pat.states.cardTitle")}>
            <Alert tone="error">{tx("pat.states.cardError")}</Alert>
          </Card>,
        ]}
      />

      <Row
        name={tx("pat.states.badge")}
        cells={[
          <span className="pat__row">
            <Chip tone="accent">{tx("pat.sample.epic")}</Chip>
            <span className="b-badge">3</span>
          </span>,
          <NA why={tx("pat.states.na_.badgePress")} />,
          <NA why={tx("pat.states.na_.badgePress")} />,
          <NA why={tx("pat.states.na_.badgeDisabled")} />,
          <StatusPill status="in-progress" working />,
          <NA why={tx("pat.states.na_.badgeEmpty")} />,
          <span className="pat__row">
            <StatusPill status="blocked" />
            <span className="b-badge bell__badge--unknown" title={tx("logs.state.unreadable")}>
              ?
            </span>
          </span>,
        ]}
      />

      <Row
        name={tx("pat.states.tab")}
        cells={[
          <Tabs>
            <Tab label="#1" selected />
            <Tab label="#2" />
          </Tabs>,
          <Live>
            <Tabs>
              <Tab label="#1" selected />
              <Tab label="#2" />
            </Tabs>
          </Live>,
          <Live act="press">
            <Tabs>
              <Tab label="#1" selected />
              <Tab label="#2" />
            </Tabs>
          </Live>,
          <Tabs>
            <Tab label="#1" selected />
            <Tab label="#2" disabled />
          </Tabs>,
          <Loading text={tx("pat.states.sessionsLoading")} />,
          <SessionTabs sessions={[]} selected="" onSelect={() => undefined} footprintCount={null} />,
          <Alert tone="error">{tx("pat.states.sessionsError")}</Alert>,
        ]}
      />

      <Row
        name={tx("pat.states.table")}
        cells={[
          <MiniTable row="selected" />,
          <Live>
            <MiniTable />
          </Live>,
          <NA why={tx("pat.states.na_.tablePress")} />,
          <MiniTable row="disabled" />,
          <MiniTable
            body={
              <StateRow>
                <Loading />
              </StateRow>
            }
          />,
          <MiniTable
            body={
              <StateRow>
                <EmptyState title={tx("pat.states.tableEmpty")} desc={tx("pat.states.tableEmptyDesc")} />
              </StateRow>
            }
          />,
          <MiniTable
            body={
              <StateRow>
                <Alert tone="error">{tx("pat.states.tableError")}</Alert>
              </StateRow>
            }
          />,
        ]}
      />

      <Row
        name={tx("pat.states.dock")}
        cells={[
          <Dock>{LINE}</Dock>,
          <Live>
            <Dock>{LINE}</Dock>
          </Live>,
          <NA why={tx("pat.states.na_.dockPress")} />,
          <Dock foot={<p className="caption">{tx("pat.states.dockEnded")}</p>}>{LINE}</Dock>,
          <Dock>
            <Loading className="cdock__pad" />
          </Dock>,
          <Dock>
            <EmptyState title={tx("pat.states.noSessions")} desc={tx("pat.states.noSessionsDesc")} />
          </Dock>,
          <Dock>
            <Alert tone="error">{tx("pat.states.logError")}</Alert>
          </Dock>,
        ]}
      />

      <h3>{tx("pat.states.navMenuTitle")}</h3>
      <p className="muted">{tx("pat.states.navMenuLead")}</p>
      <div className="pat__row pat__navmenus">
        <ThemeToggle />
        <span className="pat__navmenu">
          <ThemeToggle forceOpen />
        </span>
      </div>

      <h3>{tx("pat.states.myTurnTitle")}</h3>
      <p className="muted">{tx("pat.states.myTurnLead")}</p>
      <div className="pat__row pat__navmenus">
        <MyTurnPanel inline state={{ kind: "ok", rows: { questions: [SAMPLE_Q], mine: [SAMPLE_MINE] } }} />
        <MyTurnPanel inline state={{ kind: "ok", rows: { questions: [], mine: [] } }} />
        <MyTurnPanel inline state={{ kind: "loading" }} />
        <MyTurnPanel inline state={{ kind: "error", msg: tx("pat.states.myTurnErr") }} onRetry={() => {}} />
      </div>

      <Row
        name={tx("pat.states.nav")}
        cells={[
          <NavBar />,
          <Live>
            <NavBar />
          </Live>,
          <Live act="press">
            <NavBar />
          </Live>,
          <NA why={tx("pat.states.na_.navDisabled")} />,
          <NA why={tx("pat.states.na_.navLoading")} />,
          <NavBar>
            <BellButton count={{ unread: 0, alert: 0 }} />
          </NavBar>,
          <NavBar>
            <BellButton count="error" />
          </NavBar>,
        ]}
      />
    </>
  );
}
